import { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, AlertTriangle, Undo2, Truck, Plus, Pencil, Receipt } from 'lucide-react'
import { toast } from 'sonner'

import {
  orders,
  products,
  type AdminOrderDetail,
  type AdminProduct,
  type OrderStatus,
} from '../api/endpoints'
import { Button } from '../components/Button'
import { Card, CardContent } from '../components/Card'
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input, Select, Textarea } from '../components/Input'
import { PageLoader } from '../components/Spinner'
import { StatusBadge } from '../components/StatusBadge'
import { formatDateTime, formatINR, formatPhone } from '../lib/format'

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<AdminOrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [newStatus, setNewStatus] = useState<OrderStatus>('paid')
  const [saving, setSaving] = useState(false)
  const [confirming, setConfirming] = useState(false)
  // Return flow state
  const [returning, setReturning] = useState(false)
  const [returnQuantities, setReturnQuantities] = useState<Record<number, number>>({})
  const [returnReason, setReturnReason] = useState('')
  const [returnAdminNote, setReturnAdminNote] = useState('')
  const [returnSaving, setReturnSaving] = useState(false)

  // Manifest shipment state
  const [manifestConfirming, setManifestConfirming] = useState(false)
  const [manifestSaving, setManifestSaving] = useState(false)

  // Add item state
  const [addingItem, setAddingItem] = useState(false)
  const [products4, setProducts4] = useState<AdminProduct[]>([])
  const [productsLoaded, setProductsLoaded] = useState(false)
  const [addProductId, setAddProductId] = useState('')
  const [addQuantity, setAddQuantity] = useState('1')
  const [addSaving, setAddSaving] = useState(false)

  // Edit-meta state (address + adminNote)
  const [editingMeta, setEditingMeta] = useState(false)
  const [metaName, setMetaName] = useState('')
  const [metaPhone, setMetaPhone] = useState('')
  const [metaLine1, setMetaLine1] = useState('')
  const [metaLine2, setMetaLine2] = useState('')
  const [metaCity, setMetaCity] = useState('')
  const [metaState, setMetaState] = useState('')
  const [metaPincode, setMetaPincode] = useState('')
  const [metaNote, setMetaNote] = useState('')
  const [metaSaving, setMetaSaving] = useState(false)

  // Cash adjustment state
  const [adjusting, setAdjusting] = useState(false)
  const [adjustAmount, setAdjustAmount] = useState('0')
  const [adjustReason, setAdjustReason] = useState('')
  const [adjustSaving, setAdjustSaving] = useState(false)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    orders
      .detail(id)
      .then((r) => {
        if (!cancelled) {
          setOrder(r.order)
          setNewStatus(r.order.status)
        }
      })
      .catch((err) => {
        toast.error('Order not found', { description: err.message })
        navigate('/orders', { replace: true })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, navigate])

  async function applyStatus() {
    if (!order) return
    setSaving(true)
    try {
      await orders.updateStatus(order.id, newStatus)
      toast.success(`Order marked ${newStatus}`)
      // Refresh
      const r = await orders.detail(order.id)
      setOrder(r.order)
    } catch (err) {
      toast.error('Failed to update status', { description: (err as Error).message })
    } finally {
      setSaving(false)
      setConfirming(false)
    }
  }

  /* When the return dialog opens, default each item's return qty
   * to its full original quantity. The user can dial it down. We
   * keep the defaults in a Map keyed by orderItemId so changes
   * don't leak between dialog opens. */
  function openReturnDialog() {
    if (!order) return
    const defaults: Record<number, number> = {}
    for (const item of order.items) {
      defaults[item.id] = item.quantity
    }
    setReturnQuantities(defaults)
    setReturnReason('')
    setReturnAdminNote('')
    setReturning(true)
  }

  function closeReturnDialog() {
    setReturning(false)
    setReturnReason('')
    setReturnAdminNote('')
  }

  /* Local total of how many units the admin is about to return. Drives
   * the "Process return" button label. */
  const returnTotal = useMemo(
    () => Object.values(returnQuantities).reduce((sum, q) => sum + (Number(q) || 0), 0),
    [returnQuantities],
  )

  async function submitReturn() {
    if (!order) return
    if (returnTotal <= 0) {
      toast.error('Pick at least one unit to return')
      return
    }
    setReturnSaving(true)
    try {
      const items = Object.entries(returnQuantities)
        .map(([id, q]) => ({ orderItemId: Number(id), quantity: Number(q), reason: returnReason.trim() || undefined }))
        .filter((i) => i.quantity > 0)
      const result = await orders.return(order.id, {
        items,
        adminNote: returnAdminNote.trim() || undefined,
      })
      toast.success(
        result.order.status === 'returned'
          ? 'All items returned — order marked returned'
          : `Partial return processed (${returnTotal} units)`,
      )
      closeReturnDialog()
      // Refresh
      const r = await orders.detail(order.id)
      setOrder(r.order)
    } catch (err) {
      toast.error('Return failed', { description: (err as Error).message })
    } finally {
      setReturnSaving(false)
    }
  }

  async function submitManifest() {
    if (!order) return
    setManifestSaving(true)
    try {
      const result = await orders.manifest(order.id)
      toast.success('Order marked as delivered')
      setOrder(result.order)
      setManifestConfirming(false)
    } catch (err) {
      toast.error('Manifest failed', { description: (err as Error).message })
    } finally {
      setManifestSaving(false)
    }
  }

  /* Add item dialog: lazy-load product list on first open. The
   * products.list() call returns up to 20 active products by default;
   * we bump limit to 100 so the dropdown covers most of the catalog. */
  function openAddItemDialog() {
    setAddingItem(true)
    setAddProductId('')
    setAddQuantity('1')
    if (productsLoaded) return
    products
      .list({ limit: 100 })
      .then((r) => {
        setProducts4(r.products.filter((p) => p.isActive))
        setProductsLoaded(true)
      })
      .catch((err) =>
        toast.error('Failed to load products', { description: err.message }),
      )
  }

  async function submitAddItem() {
    if (!order) return
    const productId = addProductId.trim()
    const quantity = parseInt(addQuantity, 10)
    if (!productId) {
      toast.error('Pick a product')
      return
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Quantity must be a positive number')
      return
    }
    setAddSaving(true)
    try {
      const result = await orders.addItem(order.id, productId, quantity)
      toast.success('Item added')
      setOrder(result.order)
      setAddingItem(false)
    } catch (err) {
      toast.error('Add item failed', { description: (err as Error).message })
    } finally {
      setAddSaving(false)
    }
  }

  function openEditMetaDialog() {
    if (!order) return
    const a = (order.address ?? {}) as Record<string, string | undefined>
    setMetaName(a.full_name ?? '')
    setMetaPhone(a.phone ?? '')
    setMetaLine1(a.line1 ?? '')
    setMetaLine2(a.line2 ?? '')
    setMetaCity(a.city ?? '')
    setMetaState(a.state ?? '')
    setMetaPincode(a.pincode ?? '')
    setMetaNote(order.adminNote ?? '')
    setEditingMeta(true)
  }

  async function submitEditMeta() {
    if (!order) return
    if (order.status === 'cancelled') {
      toast.error('Cannot edit a cancelled order')
      return
    }
    if (metaName.trim().length < 2 || metaPhone.trim().length < 8 || metaLine1.trim().length < 3) {
      toast.error('Name, phone, and line1 are required')
      return
    }
    if (metaPincode.trim().length !== 6) {
      toast.error('Pincode must be 6 digits')
      return
    }
    setMetaSaving(true)
    try {
      const result = await orders.updateMeta(order.id, {
        address: {
          full_name: metaName.trim(),
          phone: metaPhone.trim(),
          line1: metaLine1.trim(),
          line2: metaLine2.trim() || undefined,
          city: metaCity.trim(),
          state: metaState.trim(),
          pincode: metaPincode.trim(),
          country: 'India',
        },
        adminNote: metaNote.trim() || null,
      })
      toast.success('Order updated')
      setOrder(result.order)
      setEditingMeta(false)
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message })
    } finally {
      setMetaSaving(false)
    }
  }

  /** Cash adjustment dialog — pre-fills with the existing
   *  adjustmentAmount / adjustmentNote (in paise, divided by 100 for
   *  rupee display). Submitting 0 explicitly clears the adjustment. */
  function openAdjustDialog() {
    if (!order) return
    const rupees = (order.adjustmentAmount ?? 0) / 100
    setAdjustAmount(String(rupees))
    setAdjustReason(order.adjustmentNote ?? '')
    setAdjusting(true)
  }

  async function submitAdjust() {
    if (!order) return
    const rupees = parseFloat(adjustAmount)
    if (!Number.isFinite(rupees)) {
      toast.error('Enter a valid amount (positive for surcharge, negative for credit)')
      return
    }
    const paise = Math.round(rupees * 100)
    setAdjustSaving(true)
    try {
      const result = await orders.setAdjustment(order.id, {
        adjustmentAmount: paise,
        adjustmentNote: adjustReason.trim() || null,
      })
      toast.success('Adjustment saved')
      setOrder(result.order)
      setAdjusting(false)
    } catch (err) {
      toast.error('Adjustment failed', { description: (err as Error).message })
    } finally {
      setAdjustSaving(false)
    }
  }

  if (loading || !order) return <PageLoader />

  const subtotal = order.subtotal
  const shipping = order.shipping
  const gst = order.gstAmount
  const addr = order.address as Record<string, string | undefined>

  return (
    <div className="space-y-5 max-w-5xl">
      <Link
        to="/orders"
        className="text-sm text-slatey hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="size-3.5" /> Back to orders
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-semibold text-ink font-mono">
              {order.orderNumber}
            </h1>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-sm text-slatey mt-1">Placed {formatDateTime(order.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Manifest button — primary action for paid orders. Hidden
              when the order is already delivered (no-op). Hidden for
              created/cancelled/returned (server rejects anyway). */}
          {order.status === 'paid' ? (
            <Button size="sm" onClick={() => setManifestConfirming(true)}>
              <Truck className="size-3.5" /> Manifest shipment
            </Button>
          ) : null}
          {/* Add item — only on orders that can still accept line
              items (cancelled/returned can't). */}
          {['created', 'paid', 'delivered'].includes(order.status) ? (
            <Button variant="outline" size="sm" onClick={openAddItemDialog}>
              <Plus className="size-3.5" /> Add item
            </Button>
          ) : null}
          {/* Edit order — always available except for cancelled. */}
          {order.status !== 'cancelled' ? (
            <Button variant="outline" size="sm" onClick={openEditMetaDialog}>
              <Pencil className="size-3.5" /> Edit order
            </Button>
          ) : null}
          {/* Adjust total — opens the cash adjustment dialog. Hidden
              for cancelled orders (no point adjusting a cancelled order). */}
          {order.status !== 'cancelled' ? (
            <Button variant="outline" size="sm" onClick={openAdjustDialog}>
              <Receipt className="size-3.5" /> Adjust total
            </Button>
          ) : null}
          {(order.status === 'delivered' || order.status === 'returned') ? (
            <Button variant="outline" size="sm" onClick={openReturnDialog}>
              <Undo2 className="size-3.5" /> Return items
            </Button>
          ) : null}
          <Dialog open={confirming} onOpenChange={setConfirming}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Save className="size-3.5" /> Change status
              </Button>
            </DialogTrigger>
            <DialogContent title="Update order status" description={`Order ${order.orderNumber}`}>
              <FormField label="New status" required>
                <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value as OrderStatus)}>
                  <option value="created">Created</option>
                  <option value="paid">Paid</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </FormField>
              {newStatus === 'cancelled' ? (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-danger flex items-start gap-2">
                  <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                  Cancelling will set the cancelled timestamp and prevent further status changes
                  from this dialog.
                </div>
              ) : null}
              <div className="mt-5 flex justify-end gap-2">
                <DialogClose asChild>
                  <Button variant="ghost" size="sm">
                    Cancel
                  </Button>
                </DialogClose>
                <Button onClick={applyStatus} loading={saving} size="sm">
                  Apply
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="font-display text-base font-semibold text-ink">Items</h2>
          </div>
          <div className="divide-y divide-line">
            {order.items.map((item) => (
              <div key={item.id} className="px-5 py-3 flex items-center gap-3">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="size-12 rounded-md object-cover bg-ivory"
                  />
                ) : (
                  <div className="size-12 rounded-md bg-ivory" />
                )}
                <div className="flex-1">
                  <p className="text-sm text-ink">{item.name}</p>
                  <p className="text-xs text-slatey">
                    {formatINR(item.price)} × {item.quantity}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-ink">
                    {formatINR(item.price * item.quantity)}
                  </p>
                  {item.gstAmount ? (
                    <p className="text-[10px] text-slatey">
                      GST {formatINR(item.gstAmount)}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-4 bg-ivory/50 border-t border-line space-y-1.5 text-sm">
            <Row label="Subtotal" value={formatINR(subtotal)} />
            <Row label="Shipping" value={shipping === 0 ? 'Free' : formatINR(shipping)} />
            <Row label="GST" value={formatINR(gst)} />
            <Row label="Total" value={formatINR(order.amount)} bold />
          </div>
        </Card>

        <div className="space-y-4">
          <Card>
            <div className="px-5 py-4 border-b border-line">
              <h2 className="font-display text-sm font-semibold text-ink uppercase tracking-wider">
                Customer
              </h2>
            </div>
            <CardContent className="pt-4 space-y-1.5 text-sm">
              <p className="text-ink font-medium">{order.customer.name}</p>
              <p className="text-slatey">{formatPhone(order.customer.phone)}</p>
              {order.customer.email ? <p className="text-slatey">{order.customer.email}</p> : null}
              {order.createdByAdmin ? (
                <p className="text-xs text-amber mt-2">Created by admin</p>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <div className="px-5 py-4 border-b border-line">
              <h2 className="font-display text-sm font-semibold text-ink uppercase tracking-wider">
                Shipping address
              </h2>
            </div>
            <CardContent className="pt-4 space-y-1 text-sm">
              <p className="text-ink font-medium">{addr.name}</p>
              <p className="text-slatey">{addr.line1}</p>
              {addr.line2 ? <p className="text-slatey">{addr.line2}</p> : null}
              <p className="text-slatey">
                {addr.city}, {addr.state} {addr.pincode}
              </p>
              <p className="text-slatey">{addr.country}</p>
            </CardContent>
          </Card>
          <Card>
            <div className="px-5 py-4 border-b border-line">
              <h2 className="font-display text-sm font-semibold text-ink uppercase tracking-wider">
                Payment & dispatch
              </h2>
            </div>
            <CardContent className="pt-4 space-y-2 text-sm">
              <Row label="Paid at" value={formatDateTime(order.paidAt)} />
              <Row label="Delivered at" value={formatDateTime(order.deliveredAt)} />
              <Row label="Cancelled at" value={formatDateTime(order.cancelledAt)} />
              {order.adminNote ? (
                <div className="mt-3 pt-3 border-t border-line">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slatey">
                    Admin note
                  </p>
                  <p className="mt-1 text-ink whitespace-pre-wrap">{order.adminNote}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Return items dialog — appears for delivered (and partially-
          returned) orders. Lets the admin pick how many units of each
          line item are being returned, with an optional reason +
          admin note. Submits to POST /api/admin/orders/:id/return. */}
      <Dialog open={returning} onOpenChange={(o) => !o && closeReturnDialog()}>
        <DialogContent
          title="Return items"
          description={`Order ${order.orderNumber} — pick how many units of each item are being returned. Stock goes back to the original batches; status flips to "Returned" only when everything is fully returned.`}
        >
          <div className="space-y-4">
            <div className="rounded-md border border-line divide-y divide-line">
              {order.items.map((item) => (
                <div key={item.id} className="px-3 py-2 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink truncate">{item.name}</p>
                    <p className="text-xs text-slatey">
                      Ordered: {item.quantity} · {formatINR(item.price)} each
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={item.quantity}
                    value={returnQuantities[item.id] ?? 0}
                    onChange={(e) => {
                      const v = Math.max(
                        0,
                        Math.min(item.quantity, parseInt(e.target.value, 10) || 0),
                      )
                      setReturnQuantities((prev) => ({ ...prev, [item.id]: v }))
                    }}
                    className="w-20 text-center"
                    aria-label={`Return quantity for ${item.name}`}
                  />
                  <span className="text-xs text-slatey w-8">/{item.quantity}</span>
                </div>
              ))}
            </div>

            <FormField
              label="Reason (optional)"
              hint="e.g. damaged, wrong size, customer changed mind. Applied to every item in this return."
            >
              <Textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                rows={2}
                maxLength={200}
                placeholder="What was wrong with the returned units?"
              />
            </FormField>

            <FormField
              label="Admin note (optional)"
              hint="Internal note for the audit log. Not shown to the customer."
            >
              <Textarea
                value={returnAdminNote}
                onChange={(e) => setReturnAdminNote(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="Any internal context..."
              />
            </FormField>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-line">
              <p className="text-xs text-slatey">
                {returnTotal === 0
                  ? 'Pick quantities above'
                  : `${returnTotal} unit${returnTotal === 1 ? '' : 's'} will be returned`}
              </p>
              <div className="flex gap-2">
                <DialogClose asChild>
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button onClick={submitReturn} loading={returnSaving} disabled={returnTotal === 0}>
                  Process return
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manifest shipment — confirms before flipping paid → delivered. */}
      <Dialog open={manifestConfirming} onOpenChange={setManifestConfirming}>
        <DialogContent
          title={`Manifest ${order.orderNumber}?`}
          description="Marks the order as delivered, stamps deliveredAt, and locks the status. Use this when the shipment has left the warehouse."
        >
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-dark flex items-start gap-2">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              This action is logged and the inventory allocations stay
              in place. Customers see the new status immediately.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button onClick={submitManifest} loading={manifestSaving}>
                <Truck className="size-4" /> Mark as delivered
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add item — pick from active products, server resolves price. */}
      <Dialog open={addingItem} onOpenChange={setAddingItem}>
        <DialogContent
          title={`Add item to ${order.orderNumber}`}
          description="The server uses the current product price. If the order is paid/delivered, stock is allocated immediately via FIFO."
        >
          <div className="space-y-4">
            <FormField label="Product" required>
              <Select
                value={addProductId}
                onChange={(e) => setAddProductId(e.target.value)}
                required
              >
                <option value="">— Pick a product —</option>
                {products4.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {formatINR(p.price)}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Quantity" required>
              <Input
                type="number"
                min={1}
                max={100}
                value={addQuantity}
                onChange={(e) => setAddQuantity(e.target.value)}
                required
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={submitAddItem} loading={addSaving} disabled={!addProductId}>
                <Plus className="size-4" /> Add to order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit order — fix address typos or attach an admin note. */}
      <Dialog open={editingMeta} onOpenChange={setEditingMeta}>
        <DialogContent
          title={`Edit ${order.orderNumber}`}
          description="Update shipping address and/or attach an internal admin note. The customer's view is unaffected until you save."
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Full name" required>
                <Input value={metaName} onChange={(e) => setMetaName(e.target.value)} required />
              </FormField>
              <FormField label="Phone" required>
                <Input value={metaPhone} onChange={(e) => setMetaPhone(e.target.value)} required />
              </FormField>
              <FormField label="Address line 1" required className="col-span-2">
                <Input value={metaLine1} onChange={(e) => setMetaLine1(e.target.value)} required />
              </FormField>
              <FormField label="Address line 2" className="col-span-2">
                <Input value={metaLine2} onChange={(e) => setMetaLine2(e.target.value)} />
              </FormField>
              <FormField label="City" required>
                <Input value={metaCity} onChange={(e) => setMetaCity(e.target.value)} required />
              </FormField>
              <FormField label="State" required>
                <Input value={metaState} onChange={(e) => setMetaState(e.target.value)} required />
              </FormField>
              <FormField label="Pincode" required>
                <Input
                  value={metaPincode}
                  onChange={(e) => setMetaPincode(e.target.value)}
                  maxLength={6}
                  required
                />
              </FormField>
            </div>
            <FormField label="Admin note (internal)" hint="Stored on the order, not shown to the customer. Useful for ops handoff.">
              <Textarea
                value={metaNote}
                onChange={(e) => setMetaNote(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="e.g. customer requested gift wrap, ship with handwritten card"
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={submitEditMeta} loading={metaSaving}>
                <Save className="size-4" /> Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash adjustment dialog — opens with the current adjustment
          pre-filled (in ₹) so the admin can edit-in-place. Positive
          numbers add to the total; negative numbers subtract. The server
          recomputes `amount` after the patch. */}
      <Dialog open={adjusting} onOpenChange={setAdjusting}>
        <DialogContent
          title={`Adjust total — ${order.orderNumber}`}
          description="Add a surcharge or issue a credit. The order's total updates immediately."
        >
          <div className="space-y-4">
            <div className="rounded-md border border-line bg-ivory/50 p-3 text-xs text-slatey space-y-0.5">
              <p>Current subtotal: <span className="font-medium text-ink">{formatINR(order.subtotal)}</span></p>
              <p>Current GST: <span className="font-medium text-ink">{formatINR(order.gstAmount)}</span></p>
              <p>Current shipping: <span className="font-medium text-ink">{order.shipping === 0 ? 'Free' : formatINR(order.shipping)}</span></p>
              {order.adjustmentAmount && order.adjustmentAmount !== 0 ? (
                <p>
                  Current adjustment:{' '}
                  <span className="font-medium text-ink">
                    {formatINR(order.adjustmentAmount)}
                  </span>
                  {order.adjustmentNote ? ` (${order.adjustmentNote})` : ''}
                </p>
              ) : null}
            </div>
            <FormField
              label="Adjustment amount (₹)"
              hint="Positive = surcharge. Negative = credit. Set to 0 to clear."
            >
              <Input
                type="number"
                step="0.01"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
              />
            </FormField>
            <FormField
              label="Reason (optional)"
              hint="Visible to admins in the order detail."
            >
              <Textarea
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="e.g. loyalty credit, comp for delay"
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={submitAdjust} loading={adjustSaving}>
                <Receipt className="size-4" /> Apply adjustment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'text-ink font-semibold' : 'text-slatey'}>{label}</span>
      <span className={bold ? 'text-ink font-semibold' : 'text-ink'}>{value}</span>
    </div>
  )
}